import { useEffect, useSyncExternalStore } from "react";

const STORAGE_KEY = "rythugaru-trip-plan-v1";

export interface TripPlanState {
  dayCount: number;
  activeDay: number;
  plan: Record<number, string[]>;
}

export const MAX_TRIP_DAYS = 4;

const DEFAULT_STATE: TripPlanState = {
  dayCount: 2,
  activeDay: 0,
  plan: { 0: [], 1: [] },
};

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function sanitize(value: unknown): TripPlanState {
  if (!value || typeof value !== "object") return DEFAULT_STATE;
  const raw = value as Partial<TripPlanState>;

  let dayCount = typeof raw.dayCount === "number" ? Math.round(raw.dayCount) : DEFAULT_STATE.dayCount;
  dayCount = clamp(dayCount, 1, MAX_TRIP_DAYS);

  const sourcePlan =
    raw.plan && typeof raw.plan === "object" ? (raw.plan as Record<string, unknown>) : {};

  const plan: Record<number, string[]> = {};
  for (let d = 0; d < dayCount; d += 1) {
    const list = sourcePlan[d];
    plan[d] = Array.isArray(list) ? list.filter((item): item is string => typeof item === "string") : [];
  }

  let activeDay = typeof raw.activeDay === "number" ? Math.round(raw.activeDay) : 0;
  activeDay = clamp(activeDay, 0, dayCount - 1);

  return { dayCount, activeDay, plan };
}

function readStored(): TripPlanState {
  if (typeof window === "undefined") return DEFAULT_STATE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    return sanitize(JSON.parse(raw));
  } catch {
    return DEFAULT_STATE;
  }
}

/**
 * The itinerary is kept in a tiny external store so every hook instance stays in
 * sync (navbar badge, preset cards, builder) instead of drifting apart.
 */
let currentState: TripPlanState = readStored();
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): TripPlanState {
  return currentState;
}

function getServerSnapshot(): TripPlanState {
  return DEFAULT_STATE;
}

function persist(next: TripPlanState) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Ignore storage write failures (private mode / quota) — the in-memory plan still works.
  }
}

function emit() {
  listeners.forEach((listener) => listener());
}

function commit(next: TripPlanState) {
  currentState = next;
  persist(next);
  emit();
}

/** Update from another tab without re-writing storage (avoids cross-tab ping-pong). */
function applyExternal(next: TripPlanState) {
  currentState = next;
  emit();
}

function setDayCount(count: number) {
  const nextCount = clamp(Math.round(count), 1, MAX_TRIP_DAYS);
  const plan: Record<number, string[]> = {};
  for (let d = 0; d < nextCount; d += 1) plan[d] = [...(currentState.plan[d] || [])];
  commit({
    dayCount: nextCount,
    activeDay: Math.min(currentState.activeDay, nextCount - 1),
    plan,
  });
}

function setActiveDay(day: number) {
  commit({ ...currentState, activeDay: clamp(day, 0, currentState.dayCount - 1) });
}

function toggleItem(id: string, day: number) {
  const plan: Record<number, string[]> = {};
  for (let d = 0; d < currentState.dayCount; d += 1) plan[d] = [...(currentState.plan[d] || [])];

  let found = -1;
  for (let d = 0; d < currentState.dayCount; d += 1) {
    if (plan[d].includes(id)) found = d;
  }

  if (found >= 0) {
    plan[found] = plan[found].filter((item) => item !== id);
  } else {
    const target = clamp(day, 0, currentState.dayCount - 1);
    plan[target] = [...plan[target], id];
  }
  commit({ ...currentState, plan });
}

function removeItem(day: number, id: string) {
  commit({
    ...currentState,
    plan: {
      ...currentState.plan,
      [day]: (currentState.plan[day] || []).filter((item) => item !== id),
    },
  });
}

function clearPlan() {
  const plan: Record<number, string[]> = {};
  for (let d = 0; d < currentState.dayCount; d += 1) plan[d] = [];
  commit({ ...currentState, plan });
}

/** Replace the whole itinerary in one shot (used when loading a preset). */
function loadPlan(dayCount: number, plan: Record<number, string[]>) {
  const count = clamp(Math.round(dayCount), 1, MAX_TRIP_DAYS);
  const next: Record<number, string[]> = {};
  for (let d = 0; d < count; d += 1) next[d] = [...(plan[d] || [])];
  commit({ dayCount: count, activeDay: 0, plan: next });
}

export function countStops(plan: Record<number, string[]>, dayCount: number) {
  let total = 0;
  for (let d = 0; d < dayCount; d += 1) total += (plan[d] || []).length;
  return total;
}

export function useTripPlan() {
  const state = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) applyExternal(readStored());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return {
    dayCount: state.dayCount,
    activeDay: state.activeDay,
    plan: state.plan,
    totalStops: countStops(state.plan, state.dayCount),
    setDayCount,
    setActiveDay,
    toggleItem,
    removeItem,
    clearPlan,
    loadPlan,
  };
}